import React, { useState } from 'react';
import Swal from 'sweetalert2';
import { SimpleAlert, LoadingGrid } from '../../../../../rbts/helpers';
import { NivelEntradaModel } from '../models/NivelEntradaModel';
import { useObtenerNiveles } from '../hooks/EventosBoleteraHook';
import { eliminarNivel, actualizarNivel } from '../../../../service/boletera/EventosBoletera';
import { ModalNivel } from '../_modals/ModalNivel';

type Props = { eventoId: number };

const MODO_LABEL: Record<string, string> = {
  numerado: 'Numerado',
  general_admission: 'Admisión general',
};

const NivelesEntrada: React.FC<Props> = ({ eventoId }) => {
  const [reload, setReload] = useState(0);
  const { resumen, loading } = useObtenerNiveles(eventoId, reload);
  const [showModal, setShowModal] = useState(false);
  const [nivelSeleccionado, setNivelSeleccionado] = useState<NivelEntradaModel | null>(null);

  const abrirAgregar = () => { setNivelSeleccionado(null); setShowModal(true); };
  const abrirEditar = (n: NivelEntradaModel) => { setNivelSeleccionado(n); setShowModal(true); };

  const confirmarEliminar = (n: NivelEntradaModel) => {
    Swal.fire({
      title: `¿Eliminar nivel "${n.nombre}"?`,
      text: 'Esta acción no se puede deshacer.',
      icon: 'warning',
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
      confirmButtonText: 'Eliminar',
      reverseButtons: true,
      customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-light me-3' },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await eliminarNivel(eventoId, n.id);
          setReload((r) => r + 1);
        } catch (error: any) {
          SimpleAlert(error?.response?.data?.message || 'Error al eliminar el nivel.', 3000, 'error');
        }
      }
    });
  };

  const confirmarDesactivar = (n: NivelEntradaModel) => {
    Swal.fire({
      title: `¿Desactivar nivel "${n.nombre}"?`,
      text: 'El nivel no estará disponible para nuevas ventas, pero los boletos ya vendidos se conservan.',
      icon: 'warning',
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
      confirmButtonText: 'Desactivar',
      reverseButtons: true,
      customClass: { confirmButton: 'btn btn-warning', cancelButton: 'btn btn-light me-3' },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await actualizarNivel(eventoId, n.id, { activo: false } as any);
          setReload((r) => r + 1);
        } catch (error: any) {
          SimpleAlert(error?.response?.data?.message || 'Error al desactivar el nivel.', 3000, 'error');
        }
      }
    });
  };

  const { rows, aforo_max_evento, asignado, disponible } = resumen;

  const bannerColor = disponible < 0 ? 'alert-danger' : disponible === 0 ? 'alert-warning' : 'alert-info';

  return (
    <div>
      {/* ── Banner de aforo ── */}
      <div className={`alert ${bannerColor} d-flex align-items-center mb-5 py-3 px-4`}>
        <span className="fw-bold me-2">Aforo del evento:</span>
        <span className="me-4">{aforo_max_evento.toLocaleString()}</span>
        <span className="fw-bold me-2">Asignado en niveles:</span>
        <span className={`me-4 ${disponible < 0 ? 'text-danger fw-bold' : ''}`}>{asignado.toLocaleString()}</span>
        <span className="fw-bold me-2">Disponible para asignar:</span>
        <span className={disponible < 0 ? 'text-danger fw-bold' : ''}>{disponible.toLocaleString()}</span>
      </div>

      {/* ── Botón agregar ── */}
      <div className="d-flex justify-content-end mb-4">
        <button className="btn btn-primary btn-sm" onClick={abrirAgregar}>
          + Agregar nivel
        </button>
      </div>

      {/* ── Tabla ── */}
      {loading ? (
        <LoadingGrid text="Cargando niveles..." />
      ) : rows.length === 0 ? (
        <div className="text-center text-muted py-10">
          No hay niveles de entrada. Agrega el primero con el botón de arriba.
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
            <thead>
              <tr className="fw-bolder text-muted">
                <th>NOMBRE</th>
                <th>PRECIO</th>
                <th>MODO DE VENTA</th>
                <th>CANT. BOLETOS</th>
                <th>VENDIDOS</th>
                <th>DISPONIBLES</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((n) => (
                <tr key={n.id}>
                  <td className="fw-bold">{n.nombre}</td>
                  <td>${Number(n.precio).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
                  <td>
                    <span className={`badge ${n.modo_venta === 'numerado' ? 'badge-light-success' : 'badge-light-info'}`}>
                      {MODO_LABEL[n.modo_venta]}
                    </span>
                  </td>
                  <td>{Number(n.cantidad_boletos).toLocaleString()}</td>
                  <td>{(n.vendidos ?? 0).toLocaleString()}</td>
                  <td>{Math.max(0, Number(n.cantidad_boletos) - (n.vendidos ?? 0) - (n.en_hold ?? 0)).toLocaleString()}</td>
                  <td className="text-end">
                    <button className="btn btn-icon btn-sm btn-light-primary me-1" title="Editar" onClick={() => abrirEditar(n)}>
                      <i className="bi bi-pencil fs-6" />
                    </button>
                    {n.activo === false ? (
                      <span className="badge badge-light-secondary">Desactivado</span>
                    ) : (n.vendidos ?? 0) > 0 ? (
                      <button className="btn btn-icon btn-sm btn-light-warning" title="Desactivar" onClick={() => confirmarDesactivar(n)}>
                        <i className="bi bi-pause-circle fs-6" />
                      </button>
                    ) : (
                      <button className="btn btn-icon btn-sm btn-light-danger" title="Eliminar" onClick={() => confirmarEliminar(n)}>
                        <i className="bi bi-trash fs-6" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ModalNivel
        show={showModal}
        eventoId={eventoId}
        nivel={nivelSeleccionado}
        aforoDisponible={disponible}
        handleClose={(refresh) => {
          setShowModal(false);
          setNivelSeleccionado(null);
          if (refresh) setReload((r) => r + 1);
        }}
      />
    </div>
  );
};

export { NivelesEntrada };
